import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SectionShot } from "@/components/marketing/CapabilityPage";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

const I18N = "features.pages.production.mapExplainer";

/** The four steps of the example. Order is the point of the picture. */
const STEPS = ["cut", "bend", "weld", "inspect"] as const;

/** What reads the map — the three things the page's sections are about. */
const READERS = ["floor", "capacity", "quality"] as const;

/**
 * „Mapa výroby" — what the word means, before the page uses it (KAN-1950).
 *
 * The production page leans on "the map" in four places ("draw the flow
 * once", "an inspection is an operation on the map", "put it on the
 * map", "map your own shop") and a reader who runs a workshop, not
 * software, had no way to know it is the screen where you lay out the
 * steps an order goes through (audit #70). The owner asked for a
 * picture rather than a paragraph: one row of steps, then what reads
 * them, then the real screen underneath.
 *
 * The diagram is markup, not an image, so it is translated with the rest
 * of the page and reflows to a column on a phone.
 */
export function ProductionMapExplainer(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  return (
    <section className="section map-explainer" data-testid="production-map-explainer">
      <div className="inner">
        <p className="mx-kicker">{t(`${I18N}.kicker`)}</p>
        <h2 className="h2">{t(`${I18N}.heading`)}</h2>
        <p className="lead">{t(`${I18N}.lead`)}</p>

        <figure className="mx-figure">
          <ol className="mx-steps" aria-label={t(`${I18N}.stepsLabel`)}>
            {STEPS.map((step, index) => (
              <li key={step} className={`mx-step mx-${step}`}>
                <span className="mx-num" aria-hidden="true">
                  {index + 1}
                </span>
                <b>{t(`${I18N}.steps.${step}.name`)}</b>
                <span className="mx-where">{t(`${I18N}.steps.${step}.where`)}</span>
              </li>
            ))}
          </ol>
          <p className="mx-bracket">{t(`${I18N}.readBy`)}</p>
          <ul className="mx-readers">
            {READERS.map((reader) => (
              <li key={reader}>
                <b>{t(`${I18N}.readers.${reader}.title`)}</b>
                <span>{t(`${I18N}.readers.${reader}.body`)}</span>
              </li>
            ))}
          </ul>
        </figure>

        <p className="mx-caption">{t(`${I18N}.shotCaption`)}</p>
        <SectionShot
          id="production-map"
          lang={lang}
          alt={t(`${I18N}.shotAlt`)}
          testId="capability-shot-map"
        />
      </div>
    </section>
  );
}
